/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { DefaultButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

import { CreateTemplateInstanceWizardSteps } from '../../../apollo/reactiveVariables/TemplateInstanceVariables';
import { getButtonIdByCurrentStep } from '../../../utilities/TemplateUtils';
import { getButtonStyles, getLabelStyles, getStackStyles } from './WizardStepLabel.styles';

export interface WizardStepLabelProps {
    label: string;
    name: string;
    isCurrent?: boolean;
    isDisabled?: boolean;
    stepKey: CreateTemplateInstanceWizardSteps;
    onClick?: (stepKey: CreateTemplateInstanceWizardSteps) => void;
    ariaLevel?: number;
}

export const WizardStepLabel: React.FC<WizardStepLabelProps> = (props: WizardStepLabelProps): JSX.Element => {
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    const { label, name, isCurrent, isDisabled, onClick, stepKey } = props;

    return (
        <DefaultButton
            role='tab'
            tabIndex={isCurrent ? 0 : -1}
            aria-selected={!!isCurrent}
            aria-controls={`panel-${stepKey}`}
            id={getButtonIdByCurrentStep(stepKey)}
            disabled={isDisabled}
            onClick={(): void => {
                if (!isDisabled && !isCurrent) {
                    onClick(stepKey);
                }
            }}
            styles={getButtonStyles(theme)}>
            <Stack horizontal verticalAlign='center' styles={getStackStyles(theme, isCurrent, isDisabled)}>
                <Stack
                    aria-hidden
                    horizontalAlign='center'
                    verticalAlign='center'
                    styles={getLabelStyles(theme, isCurrent)}>
                    {label}
                </Stack>
                <span aria-label={name}>{name}</span>
            </Stack>
        </DefaultButton>
    );
};
